import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Building, 
  Clock, 
  DollarSign, 
  FileText, 
  GraduationCap, 
  MapPin, 
  Star, 
  Calendar, 
  Home, 
  ExternalLink,
  Download
} from 'lucide-react';
import Header from '../components/Header';
import Button from '../components/ui/Button';
import { jsPDF } from 'jspdf';

// Simulated career paths data
const careerPathsData = {
  'data-science': {
    title: 'Data Science',
    description: 'Data science is an interdisciplinary field that uses scientific methods, processes, algorithms and systems to extract knowledge and insights from structured and unstructured data.',
    colleges: [
      {
        id: 'ds-col1',
        name: 'Massachusetts Institute of Technology',
        location: 'Cambridge, MA, USA',
        rating: 4.9,
        courses: [
          {
            id: 'ds-course1',
            title: 'Master of Science in Data Science',
            duration: '2 years',
            fee: '$60,000',
            scholarships: [
              'Merit Scholarship (up to 50%)',
              'Research Assistantship',
              'International Student Scholarship'
            ],
            admissionTimeline: 'Applications due by December 15, decisions by March',
            accommodation: 'On-campus and off-campus options available',
            reviews: [
              { rating: 5, text: 'Excellent program with top faculty and research opportunities.' },
              { rating: 4, text: 'Challenging but rewarding curriculum. Great job placement.' }
            ]
          },
          {
            id: 'ds-course2',
            title: 'Professional Certificate in Data Science',
            duration: '6 months',
            fee: '$12,000',
            scholarships: ['Need-based financial aid available'],
            admissionTimeline: 'Rolling admissions',
            accommodation: 'Off-campus only',
            reviews: [
              { rating: 5, text: 'Perfect for working professionals. Well-structured program.' },
              { rating: 4, text: 'Practical, hands-on experience that helped me change careers.' }
            ]
          }
        ]
      },
      {
        id: 'ds-col2',
        name: 'Stanford University',
        location: 'Stanford, CA, USA',
        rating: 4.8,
        courses: [
          {
            id: 'ds-course3',
            title: 'MS in Statistics: Data Science',
            duration: '1.5 years',
            fee: '$55,000',
            scholarships: [
              'Graduate Fellowship',
              'Teaching Assistantship',
              'External Scholarships'
            ],
            admissionTimeline: 'Applications due by December 1, decisions by April',
            accommodation: 'On-campus and off-campus options available',
            reviews: [
              { rating: 5, text: 'Rigorous program with excellent career outcomes.' },
              { rating: 5, text: 'World-class faculty and amazing networking opportunities.' }
            ]
          }
        ]
      }
    ]
  },
  'software-dev': {
    title: 'Software Development',
    description: 'Software development is the process of conceiving, specifying, designing, programming, documenting, testing, and bug fixing involved in creating and maintaining applications, frameworks, or other software components.',
    colleges: [
      {
        id: 'sd-col1',
        name: 'Carnegie Mellon University',
        location: 'Pittsburgh, PA, USA',
        rating: 4.8,
        courses: [
          {
            id: 'sd-course1',
            title: 'Master of Software Engineering',
            duration: '16 months',
            fee: '$58,000',
            scholarships: [
              'Dean\'s Scholarship',
              'Diversity Scholarship',
              'Industrial Partnership Funding'
            ],
            admissionTimeline: 'Applications due by January 10, decisions by March',
            accommodation: 'On-campus and off-campus options available',
            reviews: [
              { rating: 5, text: 'Incredibly thorough program that prepared me for industry.' },
              { rating: 4, text: 'Challenging coursework with great career support.' }
            ]
          }
        ]
      },
      {
        id: 'sd-col2',
        name: 'University of California, Berkeley',
        location: 'Berkeley, CA, USA',
        rating: 4.7,
        courses: [
          {
            id: 'sd-course2',
            title: 'Master of Engineering in EECS',
            duration: '1 year',
            fee: '$53,000',
            scholarships: [
              'Fellowship for Exceptional Students',
              'Research Grants',
              'Corporate Sponsorships'
            ],
            admissionTimeline: 'Applications due by December 8, decisions by March 15',
            accommodation: 'Limited on-campus housing, off-campus options available',
            reviews: [
              { rating: 5, text: 'Excellent faculty and research opportunities.' },
              { rating: 4, text: 'Fast-paced but very comprehensive program.' }
            ]
          }
        ]
      }
    ]
  },
  'business': {
    title: 'Business Administration',
    description: 'Business Administration encompasses the management of business operations and resources, including strategic planning, financial management, and organizational leadership.',
    colleges: [
      {
        id: 'ba-col1',
        name: 'Harvard Business School',
        location: 'Boston, MA, USA',
        rating: 4.9,
        courses: [
          {
            id: 'ba-course1',
            title: 'Master of Business Administration (MBA)',
            duration: '2 years',
            fee: '$73,000 per year',
            scholarships: [
              'Need-based Scholarships',
              'Diversity Awards',
              'Leadership Fellowships'
            ],
            admissionTimeline: 'Round 1: September, Round 2: January, decisions within 2-3 months',
            accommodation: 'On-campus housing available for first-year students',
            reviews: [
              { rating: 5, text: 'Transformative experience with unparalleled networking.' },
              { rating: 5, text: 'Case method teaching is extremely effective for business learning.' }
            ]
          }
        ]
      }
    ]
  },
  'education': {
    title: 'Education',
    description: 'Education studies focus on teaching methods, educational psychology, curriculum development, and other aspects related to learning and instruction.',
    colleges: [
      {
        id: 'ed-col1',
        name: 'Teachers College, Columbia University',
        location: 'New York, NY, USA',
        rating: 4.7,
        courses: [
          {
            id: 'ed-course1',
            title: 'Master of Education in Curriculum and Teaching',
            duration: '2 years',
            fee: '$48,000',
            scholarships: [
              'Teachers College Scholarship',
              'Education for Equity Scholarship',
              'International Student Grant'
            ],
            admissionTimeline: 'Priority deadline: January 15, Regular deadline: April 15',
            accommodation: 'Limited on-campus housing, off-campus options in NYC',
            reviews: [
              { rating: 4, text: 'Excellent program with strong theoretical foundation.' },
              { rating: 5, text: 'Great faculty and diverse student community.' }
            ]
          }
        ]
      }
    ]
  }
};

const Dashboard: React.FC = () => {
  const { careerPathId } = useParams<{ careerPathId: string }>();
  const [selectedCareerPath, setSelectedCareerPath] = useState<any>(null);
  const [expandedCollegeId, setExpandedCollegeId] = useState<string | null>(null);
  const [expandedCourseId, setExpandedCourseId] = useState<string | null>(null);
  
  useEffect(() => {
    if (careerPathId && careerPathsData[careerPathId as keyof typeof careerPathsData]) {
      setSelectedCareerPath(careerPathsData[careerPathId as keyof typeof careerPathsData]);
    }
  }, [careerPathId]);

  if (!selectedCareerPath) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <p className="text-gray-500">Career path not found</p>
        </div>
      </div>
    );
  }

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.setTextColor(33, 64, 175); // primary-600
    doc.text(`${selectedCareerPath.title} Career Plan`, 20, 20);
    
    // Add description
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    const descLines = doc.splitTextToSize(selectedCareerPath.description, 170);
    doc.text(descLines, 20, 35);
    
    let yPos = 35 + (descLines.length * 7) + 10;
    
    // Add colleges and courses
    selectedCareerPath.colleges.forEach((college: any, i: number) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      
      doc.setFontSize(16);
      doc.setTextColor(33, 64, 175);
      doc.text(`${i + 1}. ${college.name}`, 20, yPos);
      yPos += 8;
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Location: ${college.location}`, 25, yPos);
      yPos += 6;
      doc.text(`Rating: ${college.rating}/5`, 25, yPos);
      yPos += 10;
      
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Available Courses:', 25, yPos);
      yPos += 8;
      
      college.courses.forEach((course: any, j: number) => {
        if (yPos > 250) {
          doc.addPage();
          yPos = 20;
        }
        
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text(`${j + 1}. ${course.title}`, 30, yPos);
        yPos += 6;
        
        doc.setFontSize(12);
        doc.setTextColor(100, 100, 100);
        doc.text(`Duration: ${course.duration}`, 35, yPos);
        yPos += 6;
        doc.text(`Fee: ${course.fee}`, 35, yPos);
        yPos += 6;
        doc.text(`Admission Timeline: ${course.admissionTimeline}`, 35, yPos);
        yPos += 10;
        
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text('Scholarships:', 35, yPos);
        yPos += 6;
        
        course.scholarships.forEach((scholarship: string, k: number) => {
          doc.text(`• ${scholarship}`, 40, yPos);
          yPos += 6;
        });
        
        yPos += 10;
      });
      
      yPos += 10;
    });
    
    // Add generation date
    const dateStr = new Date().toLocaleDateString();
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on ${dateStr}`, 20, 280);
    
    // Save the PDF
    doc.save(`${selectedCareerPath.title.toLowerCase().replace(/\s+/g, '-')}-career-plan.pdf`);
  };
  
  const toggleCollege = (collegeId: string) => {
    if (expandedCollegeId === collegeId) {
      setExpandedCollegeId(null);
    } else {
      setExpandedCollegeId(collegeId);
      setExpandedCourseId(null);
    }
  };
  
  const toggleCourse = (courseId: string) => {
    if (expandedCourseId === courseId) {
      setExpandedCourseId(null);
    } else {
      setExpandedCourseId(courseId);
    }
  };
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.4 } },
  };
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="mb-8"
        >
          <motion.div 
            className="flex justify-between items-center mb-6"
            variants={itemVariants}
          >
            <h1 className="text-3xl font-bold text-gray-900">{selectedCareerPath.title}</h1>
            <Button
              variant="outline"
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportToPDF}
            >
              Export Plan
            </Button>
          </motion.div>
          
          <motion.p 
            className="text-gray-600 mb-8"
            variants={itemVariants}
          >
            {selectedCareerPath.description}
          </motion.p>
          
          <motion.h2 
            className="text-2xl font-bold text-gray-900 mb-6"
            variants={itemVariants}
          >
            Recommended Colleges & Programs
          </motion.h2>
        </motion.div>
        
        <div className="space-y-6">
          {selectedCareerPath.colleges.map((college: any) => (
            <CollegeCard 
              key={college.id}
              college={college}
              isExpanded={expandedCollegeId === college.id}
              expandedCourseId={expandedCourseId}
              onToggleCollege={() => toggleCollege(college.id)}
              onToggleCourse={toggleCourse}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

interface CollegeCardProps {
  college: any;
  isExpanded: boolean;
  expandedCourseId: string | null;
  onToggleCollege: () => void;
  onToggleCourse: (courseId: string) => void;
}

const CollegeCard: React.FC<CollegeCardProps> = ({ 
  college, 
  isExpanded, 
  expandedCourseId, 
  onToggleCollege, 
  onToggleCourse 
}) => {
  return (
    <motion.div 
      className="card overflow-visible"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div 
        className="p-6 cursor-pointer"
        onClick={onToggleCollege}
      >
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{college.name}</h3>
            <div className="flex items-center mt-2 text-gray-600">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="text-sm">{college.location}</span>
            </div>
          </div>
          <div className="flex items-center">
            <div className="flex items-center mr-4">
              <Star className="h-4 w-4 text-yellow-500 mr-1" />
              <span className="font-medium">{college.rating}/5</span>
            </div>
            <div className="text-primary-600">
              {isExpanded ? 
                <span className="text-sm font-medium">Hide Programs</span> : 
                <span className="text-sm font-medium">View {college.courses.length} Programs</span>
              }
            </div>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="border-t border-gray-200"
        >
          {college.courses.map((course: any) => (
            <CourseItem
              key={course.id}
              course={course}
              isExpanded={expandedCourseId === course.id}
              onToggleCourse={() => onToggleCourse(course.id)}
            />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

interface CourseItemProps {
  course: any;
  isExpanded: boolean;
  onToggleCourse: () => void;
}

const CourseItem: React.FC<CourseItemProps> = ({ course, isExpanded, onToggleCourse }) => {
  return (
    <div className="border-b border-gray-200 last:border-b-0">
      <div 
        className="p-6 cursor-pointer hover:bg-gray-50"
        onClick={onToggleCourse}
      >
        <div className="flex justify-between items-start">
          <div>
            <h4 className="font-medium text-gray-900">{course.title}</h4>
            <div className="flex flex-wrap mt-2 gap-x-4 gap-y-2 text-sm text-gray-600">
              <div className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                <span>{course.duration}</span>
              </div>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 mr-1" />
                <span>{course.fee}</span>
              </div>
            </div>
          </div>
          <div>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                window.open('https://example.com/apply', '_blank');
              }}
              icon={<ExternalLink className="h-3 w-3" />}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
      
      {isExpanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.3 }}
          className="px-6 pb-6"
        >
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-primary-600" />
                Admission Details
              </h5>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">Timeline:</p>
                  <p className="text-gray-700">{course.admissionTimeline}</p>
                </div>
                <div>
                  <p className="text-gray-500">Accommodation:</p>
                  <p className="text-gray-700">{course.accommodation}</p>
                </div>
              </div>
            </div>
            
            <div>
              <h5 className="font-medium text-gray-900 mb-3 flex items-center">
                <DollarSign className="h-4 w-4 mr-2 text-secondary-600" />
                Scholarships Available
              </h5>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                {course.scholarships.map((scholarship: string, index: number) => (
                  <li key={index}>{scholarship}</li>
                ))}
              </ul>
            </div>
          </div>
          
          <div className="mt-6">
            <h5 className="font-medium text-gray-900 mb-3 flex items-center">
              <Star className="h-4 w-4 mr-2 text-yellow-500" />
              Student Reviews
            </h5>
            <div className="space-y-4">
              {course.reviews.map((review: any, index: number) => (
                <div key={index} className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-600">
                        {review.rating}
                      </span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg text-sm text-gray-700 flex-grow">
                    {review.text}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default Dashboard;